import { PosterStage } from "./stage/PosterStage";
import styles from "./PosterEditor.module.css";
import { PosterProjectProvider } from "./model/poster-project-provider";
import { EditorUIProvider } from "./use-editor-ui";
import { PosterEditorToolbar } from "./sidebar/poster-editor-toolbar";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import { PosterProject } from "./model/poster-project";
import { ProjectTemplate } from "./model/project-template";
import { useState, useEffect } from "react";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { usePaths } from "BEDROCK/paths";
import { usePosterProject } from "./model/poster-project-provider";
import { ThreeDot } from "react-loading-indicators";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "hooks/useIsMobile";
import { useHasPointer } from "./hooks/useHasPointer";
import { NoPageContent } from "BEDROCK/shared-components/no-content";
import { AuthGateModal } from "BEDROCK/shared-components/auth-gate-modal";
import { TooltipProvider } from "./tooltips";
import { useNavigationBarContext } from "navigation-bar/navigation-bar-provider";
import { UIIconButton } from "./common-components/ui-icon-drawing/ui-icon-button";
import { UIIcon } from "./common-components/ui-icon-drawing/ui-icon";
import { AlertModal } from "BEDROCK/shared-components/alert-modal";
import { useAnalyticsTracker } from "analytics-tracker";

export type EditorContextType = "editing" | "rendering";

interface PosterEditorProps {
  //Will likely be passed in when used as a direct component rather than a linked page.
  //So this will probably be when it's nested in process-order.
  projectID?: string;
  contextType?: EditorContextType; //Remove
}

export const PosterEditor = (props: PosterEditorProps) => {
  const { loadProject, isError, testForDuplicates } = useProjectStore();
  const isMobile = useIsMobile(768);
  const hasPointer = useHasPointer();

  // Mobile is now supported - use isMobile for layout switching, not blocking
  const hasCompatibleDevice = true; // Previously: !isMobile && hasPointer

  const {
    loadTemplate,
    isLoading: isLoadingTemplate,
    isFetching: isFetchingTemplate,
    isError: isErrorTemplate,
  } = useTemplateStore();

  const [initialProject, setInitialProject] = useState<
    PosterProject | undefined
  >();
  const [template, setTemplate] = useState<ProjectTemplate | undefined>();
  const [contextType, setContextType] = useState<EditorContextType>("editing");

  const location = useLocation();

  //Get the id from the URL last part and load project or template

  useEffect(() => {
    const fetchData = async () => {
      setInitialProject(undefined);
      setTemplate(undefined);

      if (props.projectID) {
        const project = await loadProject(props.projectID);
        setInitialProject(project);
      } else {
        const path = window.location.pathname;
        const parts = path.split("/");
        const id = parts[parts.length - 1];
        const type = parts[parts.length - 2];
        const process = new URLSearchParams(window.location.search).get(
          "process"
        );

        if (!id) return;

        if (type === "project") {
          const project = await loadProject(id);
          setInitialProject(project);
          if (process === "true") {
            const url = new URL(window.location.href);
            url.searchParams.delete("process");
            window.history.replaceState(
              {},
              document.title,
              url.pathname + url.search
            );
            setContextType("rendering");
          }
        } else if (type === "template") {
          const template = await loadTemplate(id);
          setTemplate(template);
        }
      }
    };

    fetchData();
  }, [props.projectID, location.key]); // 👈 updated dependency array

  useEffect(() => {}, [initialProject]);
  if (!hasCompatibleDevice) {
    return <IncompatibleDeviceWarning />;
  }

  if (isLoadingTemplate || isFetchingTemplate) {
    return <Loading />;
  }

  const object = initialProject || template;

  if (!object) {
    return (
      <NoPageContent
        iconName="MAGNIFYING-EXCLAMATION"
        title="No project found"
        message="This project could not be found"
      />
    );
  }
  return (
    <PosterProjectProvider
      initialProject={template?.project ?? initialProject}
      projectTemplate={template}
    >
      <Content
        contextType={contextType}
        isMobile={isMobile}
        onSave={() => {
          setContextType("rendering");
        }}
      />
    </PosterProjectProvider>
  );
};

const NoProjectFound = () => {
  return <div>No project found</div>;
};

const Loading = () => {
  return (
    <div className={styles.loadingContainer}>
      <ThreeDot color="lightGray" size="medium" text="" textColor="" />
    </div>
  );
};

interface ContentProps {
  contextType: EditorContextType;
  isMobile: boolean;
  onSave: () => void;
}
const Content = (props: ContentProps) => {
  const { loadSignedURLS, saveDraft, project } = usePosterProject();
  const { checkout, home, navigateTo } = usePaths();
  const { isUserAuthenticated } = useAuthContext();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  const { setNavbarHidden } = useNavigationBarContext();
  const [isShowingDeleteProjectAlert, setIsShowingDeleteProjectAlert] =
    useState(false);
  const { deleteProjectAsync, projectsNotInCheckout } = useProjectStore();
  const { trackEvent } = useAnalyticsTracker();

  useEffect(() => {
    loadSignedURLS();
  }, []);

  // Hide navbar on mobile editor
  useEffect(() => {
    if (props.isMobile) {
      setNavbarHidden(true);
    }
    return () => {
      setNavbarHidden(false);
    };
  }, [props.isMobile, setNavbarHidden]);

  const handleSave = () => {
    // Always save locally first
    saveDraft();

    if (!isUserAuthenticated) {
      // Show auth modal for guests
      setShowAuthModal(true);
    } else {
      // Authenticated users proceed to rendering
      props.onSave();
    }
  };

  const deleteProjectAndGoHome = async () => {
    trackEvent("Project Deleted", {
      projectId: project.id,
      isTemplateDraft: project.isTemplateDraft,
    });

    const projects = [...projectsNotInCheckout(), project];
    await Promise.all(projects.map((proj) => deleteProjectAsync(proj)));
    navigateTo(home);
  };

  const containerClasses = props.isMobile
    ? `${styles.container} ${styles.containerMobile}`
    : styles.container;

  const sidebarClasses = props.isMobile
    ? `${styles.sidebarContainer} ${styles.sidebarContainerMobile}`
    : styles.sidebarContainer;

  const stageClasses = props.isMobile
    ? `${styles.editorStageContainer} ${styles.editorStageContainerMobile}`
    : styles.editorStageContainer;

  return (
    <TooltipProvider>
      <EditorUIProvider>
        <div className={containerClasses}>
          {/* Desktop sidebar - render first to appear on left */}
          {!props.isMobile && props.contextType !== "rendering" && (
            <div className={sidebarClasses}>
              <PosterEditorToolbar
                onClickSave={handleSave}
                isMobile={props.isMobile}
                isDrawerExpanded={isDrawerExpanded}
                onDrawerToggle={setIsDrawerExpanded}
              />
            </div>
          )}
          <div className={stageClasses}>
            <PosterStage
              onShowContextualEditor={(show) => {}}
              contextType={props.contextType}
              isMobile={props.isMobile}
              isDrawerExpanded={isDrawerExpanded}
              onDismissDrawer={() => setIsDrawerExpanded(false)}
            />
          </div>
          {/* Mobile action bar - between poster and drawer */}
          {props.isMobile && props.contextType !== "rendering" && (
            <div className={styles.mobileActionBar}>
              <UIIconButton
                iconName="BIN"
                variant="REGULAR"
                padding={8}
                onClick={() => {
                  console.log("Delete button clicked, setting alert to true");
                  setIsShowingDeleteProjectAlert(true);
                }}
              />
              <button
                className={styles.mobileCheckoutButton}
                onClick={() => {
                  trackEvent("Project Save Button clicked");
                  saveDraft();
                  handleSave();
                }}
              >
                <UIIcon iconName="BAG" variant="CONTRAST" size={22} />
                <UIIcon iconName="CHEVRON-RIGHT" variant="CONTRAST" size={18} />
              </button>
              <AlertModal
                title="Delete Project"
                message="Are you sure you want to delete this project? This action cannot be undone."
                isShowing={isShowingDeleteProjectAlert}
                onDismiss={() => setIsShowingDeleteProjectAlert(false)}
                onProceed={deleteProjectAndGoHome}
                proceedCTA="DELETE"
                dismissCTA="CANCEL"
              />
            </div>
          )}
          {/* Mobile sidebar - render after stage to appear at bottom */}
          {props.isMobile && props.contextType !== "rendering" && (
            <div className={sidebarClasses}>
              <PosterEditorToolbar
                onClickSave={handleSave}
                isMobile={props.isMobile}
                isDrawerExpanded={isDrawerExpanded}
                onDrawerToggle={setIsDrawerExpanded}
              />
            </div>
          )}
        </div>
        <AuthGateModal
          isShowing={showAuthModal}
          onDismiss={() => setShowAuthModal(false)}
          redirectPath={checkout}
        />
      </EditorUIProvider>
    </TooltipProvider>
  );
};

const IncompatibleDeviceWarning = () => {
  return (
    <div className={styles.incompatibleDeviceWarning}>
      <img
        className="incompatibleDeviceIcon"
        src="/icons/laptop.png"
        alt="Incompatible Device"
      />
      <h1>
        Trailart's editor works better on a desktop or laptop with a mouse or
        trackpad.
      </h1>
    </div>
  );
};
